import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/TextField';
import {
  adminApi,
  adminErrorText,
  inviteStatus,
  type InviteRow,
  type InviteStatus,
  type NewInvite,
} from './adminApi';
import { expiresIn, OneTimeLink } from './OneTimeLink';

const STATUS_LABEL: Record<InviteStatus, string> = {
  pending: 'Not signed up yet',
  accepted: 'Signed up',
  revoked: 'Removed',
  expired: 'Link expired',
};

/**
 * Every invite the server keeps (conventions §14), with its status. Adding an email, or asking for
 * a new link on a removed or expired invite, returns a one-time link that is shown here once.
 */
export function InvitesPanel() {
  const [invites, setInvites] = useState<InviteRow[] | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<NewInvite | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.listInvites().then(setInvites, (err: unknown) => setError(adminErrorText(err)));
  }, []);

  async function issue(value: string): Promise<boolean> {
    setError(null);
    setBusy(true);
    try {
      const created = await adminApi.addInvite(value);
      const { url: _url, ...row } = created;
      setInvites((list) => [row, ...(list ?? []).filter((i) => i.id !== row.id)]);
      setIssued(created);
      return true;
    } catch (err) {
      setError(adminErrorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value.includes('@')) return setError('Enter an email address.');
    if (await issue(value)) setEmail('');
  }

  async function onRevoke(row: InviteRow) {
    setError(null);
    try {
      await adminApi.revokeInvite(row.id);
      const revokedAt = new Date().toISOString();
      setInvites((list) => (list ?? []).map((i) => (i.id === row.id ? { ...i, revokedAt } : i)));
      if (issued?.id === row.id) setIssued(null);
    } catch (err) {
      setError(adminErrorText(err));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={onAdd} noValidate className="flex flex-col gap-3">
        <TextField
          label="Email to invite"
          type="email"
          autoComplete="off"
          maxLength={128}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          hint="You'll get a link to send them. They sign up from it, by password or Google."
        />
        <Button type="submit" variant="primary" disabled={busy}>
          Add invite
        </Button>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {invites === null && !error ? (
        <p role="status" className="text-muted">
          Loading invites…
        </p>
      ) : null}
      {invites?.length === 0 ? (
        <p className="text-muted">No invites yet. Add an email above.</p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {invites?.map((i) => {
          const status = inviteStatus(i);
          const link = issued?.id === i.id ? issued : null;
          return (
            <li
              key={i.id}
              aria-label={i.email}
              className="flex flex-col gap-2 rounded-2xl bg-surface-2 p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm break-all">{i.email}</p>
                  <p className="text-xs text-muted">{STATUS_LABEL[status]}</p>
                </div>
                <div className="flex gap-1">
                  {status === 'expired' || status === 'revoked' ? (
                    <Button
                      variant="ghost"
                      className="px-3"
                      aria-label={`New link for ${i.email}`}
                      disabled={busy}
                      onClick={() => void issue(i.email)}
                    >
                      New link
                    </Button>
                  ) : null}
                  {status === 'pending' || status === 'expired' ? (
                    <Button
                      variant="ghost"
                      className="px-3"
                      aria-label={`Remove ${i.email}`}
                      onClick={() => void onRevoke(i)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
              {link ? (
                <OneTimeLink
                  url={link.url}
                  note={`Send this link to ${link.email}. It works once and expires ${
                    link.expiresAt ? expiresIn(link.expiresAt) : 'in 7 days'
                  }. It won't be shown again.`}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
